/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { isStyleVisible } from "common/utils/ui/element";
import { chunkArray } from "common/utils/array";
import { isElementVisible } from "common/utils/ui/visibility";
import { productItemsPluralizer } from "common/product-items-pluralizer";
import initSameDayBadge from "components/jiffy/same-day-badge/same-day-badge";
import {
  formatDeliveryTimeRange,
  extractDayFromDate,
  shouldShowTimeRange
} from "common/utils/format/date";
import { CachedDeliveryManager } from "common/utils/delivery-date/cached-delivery-manager";
import CompositionEstimatesClass from "./composition-estimates-class";

const SELECTORS = {
  DELIVERY_CONTAINER: ".js-plp-dates-container",
  DELIVERY_DATE: ".js-plp-delivery-date",
  DELIVERY_DAY: ".js-plp-delivery-day",
  DELIVERY_FLASH_LOADER: ".js-plp-delivery-loader",
  DELIVERY_INVENTORY: ".js-plp-delivery-inventory",
  DELIVERY_SHIPPING_RATE: ".js-plp-delivery-shipping-rate",
  DELIVERY_SPINNER: ".js-plp-delivery-spinner",
  DELIVERY_TIMELEFT: ".js-plp-delivery-timeleft",
  DELIVERY_TIMELEFT_TEXT: ".js-plp-delivery-timeleft-text",
  SAME_DAY_ITEM: ".js-same-day-item",
  SAME_DAY_BADGES: ".js-same-day-item[data-same-day-supported='true']",
  DELIVERY_LOADING: ".js-plp-delivery-loading",
  DELIVERY_LOADED: ".js-plp-delivery-loaded",
  PRODUCT_CARD_DELIVERY: ".js-plp-product-card-delivery"
};

const SAME_DAY_DELIVERY_DATE = "Today";

const fetcher = new CompositionEstimatesClass();

// Global variable is used to sync different js chunks that call this module
const compositionIdsSent = window?.jiffy?.ui?.productsCompositionIdsSent || [];

// Track ongoing requests to prevent duplicates
const ongoingRequests = new Map();

// Helper function to apply cached delivery data to a row
const applyCachedDeliveryToRow = cell => {
  const $deliveryDateElements = cell.querySelectorAll(SELECTORS.DELIVERY_DATE);

  const setLoadingState = isLoading => {
    const $spinner = cell.querySelector(SELECTORS.DELIVERY_SPINNER);
    const $deliveryContainer = cell.querySelector(SELECTORS.DELIVERY_CONTAINER);

    if ($spinner) {
      $spinner.classList.toggle("hidden", !isLoading);
    }
    if ($deliveryContainer) {
      $deliveryContainer.classList.toggle("hidden", isLoading);
    }
  };

  CachedDeliveryManager.applyCachedDelivery({
    deliveryDateElements: $deliveryDateElements,
    setLoadingState
  });

  const $deliveryTimeleftTextEl = cell.querySelector(SELECTORS.DELIVERY_TIMELEFT_TEXT);
  if ($deliveryTimeleftTextEl) {
    CachedDeliveryManager.applyCachedTimeLeftText(
      $deliveryTimeleftTextEl,
      window.jiffyCachedDeliveryData.hasDeliveryStartEnd,
      window.jiffyCachedDeliveryData.cutoffLabel
    );
  }
};

// Helper function to create a unique request key
const createRequestKey = (compositionId, options) => {
  const isMade = Boolean(options?.isMade);
  const isFlash = Boolean(options?.isFlash);
  const isSameDayEstimates = Boolean(options?.isSameDayEstimates);
  const isProductCardV3 = Boolean(options?.isProductCardV3);
  const businessLine = options?.businessLine || "";
  return `${compositionId}-${isMade}-${isFlash}-${isSameDayEstimates}-${isProductCardV3}-${businessLine}`;
};

// Helper function to handle cut-off time display with show/hide logic.
// `cutoffLabel` is the server-rendered "Order by" target produced by
// `CustomerFacingCutoff.display_label` — frontend never reformats it.
const handleCutoffTimeDisplay = ($timeleftTextEl, isTimeRangeAvailable, cutoffLabel) => {
  if (!$timeleftTextEl) {
    return;
  }
  // Hide by default
  $timeleftTextEl.parentElement.classList.add("hidden");

  if (isTimeRangeAvailable && cutoffLabel) {
    // eslint-disable-next-line no-param-reassign -- intentional DOM mutation
    $timeleftTextEl.textContent = `Order by ${cutoffLabel}`;
    $timeleftTextEl.parentElement.classList.remove("hidden");
  }
};

// Helper function to check if a composition ID should be processed
const shouldProcessCompositionId = (compositionId, options) => {
  const requestKey = createRequestKey(compositionId, options);

  // Check if there's already an ongoing request
  if (ongoingRequests.has(requestKey)) {
    return false;
  }

  // For regular compositions, also check the global sent list
  const isMade = Boolean(options?.isMade);
  const isFlash = Boolean(options?.isFlash);
  if (!isMade && !isFlash && compositionIdsSent.includes(compositionId)) {
    return false;
  }

  return true;
};

// Helper function to mark a composition ID as being processed
const markCompositionIdAsProcessing = (compositionId, options) => {
  const requestKey = createRequestKey(compositionId, options);
  const isMade = Boolean(options?.isMade);
  const isFlash = Boolean(options?.isFlash);

  // Add to ongoing requests
  ongoingRequests.set(requestKey, true);

  // Add to global sent list for regular compositions
  if (!isMade && !isFlash) {
    compositionIdsSent.push(compositionId);
  }
};

// Helper function to mark a composition ID as completed
const markCompositionIdAsCompleted = (compositionId, options) => {
  const requestKey = createRequestKey(compositionId, options);
  ongoingRequests.delete(requestKey);
};

const isSpinnerHidden = $el => {
  const $spinner = $el.querySelector(".js-plp-delivery-spinner");

  // Need the negated result
  return !isStyleVisible($spinner);
};

const getCompositionIds = rows => {
  const result = Array.from(rows)
    .filter(cell => {
      // Check if assignable and doesn't have cached delivery data
      if (cell.dataset.assignable !== "true") return false;

      const hasCachedDelivery =
        cell.dataset.hasCachedDelivery === "true" ||
        cell.querySelector('[data-has-cached-delivery="true"]');
      if (hasCachedDelivery && window.jiffyCachedDeliveryData?.deliveryDate) {
        applyCachedDeliveryToRow(cell);
        return false; // Don't include in API call
      }

      return true;
    })
    .reduce(
      (acct, element) => {
        const { compositionId: id, sameDayCutoff, made, flash, businessLine } = element.dataset;
        if (flash === "true") {
          acct.flashCompositionIds.push(id);
        } else if (sameDayCutoff) {
          acct.sameDayCutoffIds.push(id);
        } else if (made === "true") {
          acct.madeCompositionIds.push(id);
        } else {
          acct.regularCompositionIds.push(id);
        }
        if (id && businessLine) {
          acct.businessLineMap[id] = businessLine;
        }
        return acct;
      },
      {
        regularCompositionIds: [],
        sameDayCutoffIds: [],
        madeCompositionIds: [],
        flashCompositionIds: [],
        businessLineMap: {}
      }
    );
  return result;
};

const groupIdsByBusinessLine = (compositionIds, businessLineMap) => {
  const groups = {};
  compositionIds.forEach(id => {
    const bl = businessLineMap[id] || "";
    if (!groups[bl]) groups[bl] = [];
    groups[bl].push(id);
  });
  return groups;
};

const renderSameDayBadge = ($container, { flashCutOffTime, deliveryDate }) => {
  const $productCard = $container.parentElement;

  const isSameDayOrNextDay =
    deliveryDate &&
    (deliveryDate.toLowerCase().includes("today") ||
      deliveryDate.toLowerCase().includes("tomorrow"));
  let formattedDate = deliveryDate;

  if (isSameDayOrNextDay) {
    formattedDate = extractDayFromDate(deliveryDate);
  }
  const badge = initSameDayBadge($productCard, {
    pageType: "plp",
    flashCutOffTime,
    deliveryDate: formattedDate
  });

  if (badge) {
    if (badge.isFlashDelivery) badge.showBadge();
  }
};

const renderSameDayEstimates = ({
  compositionId,
  flashCutOffTime,
  deliveryDate,
  deliveryTime,
  deliveryTimeEnd,
  cutoffLabel
}) => {
  const $elements = document.querySelectorAll(
    `${SELECTORS.SAME_DAY_ITEM}[data-composition-id="${compositionId}"]`
  );

  if ($elements.length === 0) {
    return;
  }

  const isSameDay = deliveryDate && deliveryDate.toLowerCase().includes("today");
  const isNextDay = deliveryDate && deliveryDate.toLowerCase().includes("tomorrow");

  $elements.forEach($element => {
    // Hide spinner - this was the missing piece for same-day estimates
    const $spinner = $element.querySelector(SELECTORS.DELIVERY_SPINNER);
    if ($spinner) {
      $spinner.classList.add("hidden");
    }

    // Also check for spinners in related delivery containers
    const $deliveryContainer = document.querySelector(
      `.product-card__delivery-row[data-composition-id="${compositionId}"]`
    );
    if ($deliveryContainer) {
      const $containerSpinner = $deliveryContainer.querySelector(SELECTORS.DELIVERY_SPINNER);
      if ($containerSpinner) {
        $containerSpinner.classList.add("hidden");
      }
      if (deliveryDate) {
        // Show delivery date in the container
        const $deliveryDateEl = $deliveryContainer.querySelector(SELECTORS.DELIVERY_DATE);
        const isTimeRangeAvailable =
          shouldShowTimeRange(deliveryDate) && deliveryTime && deliveryTimeEnd;

        if ($deliveryDateEl) {
          $deliveryDateEl.setAttribute("data-date", deliveryDate);
          $deliveryDateEl.setAttribute("data-timeleft", flashCutOffTime);

          $deliveryDateEl.innerHTML =
            isSameDay || isNextDay ? extractDayFromDate(deliveryDate) : deliveryDate;

          const $timeleftTextEl = $deliveryContainer.querySelector(
            SELECTORS.DELIVERY_TIMELEFT_TEXT
          );

          // Handle time left text - show time left when time range is showing, otherwise show "No sneaky rush fees"
          CachedDeliveryManager.applyCachedTimeLeftText(
            $timeleftTextEl,
            isTimeRangeAvailable,
            cutoffLabel
          );

          // Add tomorrow class for same-day/next-day deliveries
          $deliveryDateEl.classList.toggle(
            "product-card__delivery-date-cart--tomorrow",
            isSameDay || isNextDay
          );
        }

        const $deliveryContainerEl = $deliveryContainer.querySelector(SELECTORS.DELIVERY_CONTAINER);
        if ($deliveryContainerEl) {
          $deliveryContainerEl.classList.remove("hidden");
        }

        // Add same-day badge if it's same day or next day
        if (isSameDay || isNextDay) {
          renderSameDayBadge($deliveryContainer, {
            flashCutOffTime,
            deliveryDate
          });
        }
      } else {
        $deliveryContainer.dataset.assignable = false;
        $deliveryContainer.classList.add("hidden");
      }
    }

    if (deliveryDate) {
      const badge = initSameDayBadge($element, {
        pageType: "header",
        flashCutOffTime,
        deliveryDate
      });

      if (badge) {
        if (badge.isFlashDelivery) badge.showBadge();
      }
    }
  });
};

const renderEstimates = ({
  compositionId,
  date,
  inventory,
  oneShipDay,
  orderInTheNext,
  shippingRate,
  timeleft,
  deliveryDay,
  isMade,
  deliveryTime,
  deliveryTimeEnd,
  cutoffLabel
}) => {
  const isSameDay = date && date.toLowerCase().includes("today");
  const isNextDay = date && date.toLowerCase().includes("tomorrow");

  const renderDeliveryEstimates = $container => {
    if (!date) {
      $container.classList.add("hidden");
      // eslint-disable-next-line no-param-reassign -- intentional DOM mutation
      $container.dataset.assignable = false;
      return;
    }

    $container
      .querySelector(SELECTORS.DELIVERY_DATE)
      .classList.toggle("product-card__delivery-date-cart--tomorrow", !!oneShipDay);

    const $deliveryDateEl = $container.querySelector(SELECTORS.DELIVERY_DATE);
    const $deliveryTimeleftTextEl = $container.querySelector(SELECTORS.DELIVERY_TIMELEFT_TEXT);

    $deliveryDateEl.setAttribute("data-date", date);
    $deliveryDateEl.setAttribute("data-timeleft", orderInTheNext);
    const isTimeRangeAvailable = shouldShowTimeRange(date) && deliveryTime && deliveryTimeEnd;

    // If it's today/tomorrow and we have time range and isMade, show extracted day only
    if (isTimeRangeAvailable && isMade) {
      const formattedDate = `${extractDayFromDate(date)} ${formatDeliveryTimeRange(deliveryTime, deliveryTimeEnd)}`;
      $deliveryDateEl.innerHTML = formattedDate;
      // Handle cut-off time display - show only when time range is available and cut-off time exists
      handleCutoffTimeDisplay($deliveryTimeleftTextEl, isTimeRangeAvailable, cutoffLabel);
    } else {
      $deliveryDateEl.innerHTML = isSameDay || isNextDay ? extractDayFromDate(date) : date;
    }

    $container.querySelector(SELECTORS.DELIVERY_CONTAINER).classList.remove("hidden");
    $container.querySelector(SELECTORS.DELIVERY_SPINNER).classList.add("hidden");

    const $sameDay = $container.closest(".js-product-card")?.previousElementSibling;
    const $timeleft = $sameDay?.querySelector(SELECTORS.DELIVERY_TIMELEFT);

    if (orderInTheNext && $timeleft) {
      const timeLeftText = orderInTheNext.replace(" hours", "h").replace(" minutes", "m");
      $timeleft.dataset.countdown = timeleft;
      $timeleft.textContent = timeLeftText;
      $timeleft.parentElement.classList.remove("hidden");

      const $loader = $sameDay.querySelector(SELECTORS.DELIVERY_FLASH_LOADER);
      $loader?.classList.add("hidden");

      const $deliveryDay = $sameDay.querySelector(SELECTORS.DELIVERY_DAY);
      if ($deliveryDay) {
        $deliveryDay.textContent = deliveryDay;
        $deliveryDay.parentElement.classList.remove("hidden");
      }

      const $shipingRate = $sameDay?.querySelector(SELECTORS.DELIVERY_SHIPPING_RATE);
      if ($shipingRate) {
        $shipingRate.textContent = shippingRate;
        $shipingRate.parentElement.classList.remove("hidden");
      }
    }

    if (inventory) {
      const $inventory = $container.querySelector(SELECTORS.DELIVERY_INVENTORY);
      const { taxon } = $inventory.dataset;
      const productItems = productItemsPluralizer(inventory, taxon);
      $inventory.textContent = `Only ${productItems} left for ${deliveryDay}`;
    }
  };

  const baseSelector = `.product-card__delivery-row[data-composition-id="${compositionId}"]`;
  const selector = isMade
    ? `${baseSelector}[data-made="true"]`
    : `${baseSelector}:not([data-made="true"])`;

  const $allContainers = document.querySelectorAll(selector);

  Array.from($allContainers).forEach($container => {
    renderDeliveryEstimates($container);
    if (isSameDay || isNextDay) {
      renderSameDayBadge($container, {
        flashCutOffTime: orderInTheNext,
        deliveryDate: date
      });
    }
  });
};

const renderProductCardV3Estimates = ({
  compositionId,
  date,
  deliveryTime,
  deliveryTimeEnd,
  cutoffLabel
}) => {
  const selector = `.js-product-card-v3[data-composition-id="${compositionId}"]:not([data-is-featured="true"])`;

  const $allContainers = document.querySelectorAll(selector);

  $allContainers.forEach($container => {
    $container.querySelector(SELECTORS.DELIVERY_LOADING).classList.add("hidden");

    const $deliveryDateEl = $container.querySelector(SELECTORS.DELIVERY_DATE);
    const isTimeRangeAvailable = shouldShowTimeRange(date) && deliveryTime && deliveryTimeEnd;

    // If it's today/tomorrow and we have time range, show extracted day only
    if (isTimeRangeAvailable) {
      const formattedDate = `${extractDayFromDate(date)} ${formatDeliveryTimeRange(deliveryTime, deliveryTimeEnd)}`;
      $deliveryDateEl.innerHTML = formattedDate;
    } else {
      $deliveryDateEl.replaceChildren(date);
    }

    // Handle delivery time range for Product Card V3
    const $timeleftTextEl = $container.querySelector(SELECTORS.DELIVERY_TIMELEFT_TEXT);

    // Handle time left text - show time left when time range is showing, otherwise show "No sneaky rush fees"
    CachedDeliveryManager.applyCachedTimeLeftText(
      $timeleftTextEl,
      isTimeRangeAvailable,
      cutoffLabel
    );

    const $deliveryLoadedEl = $container.querySelector(SELECTORS.DELIVERY_LOADED);
    $deliveryLoadedEl.classList.remove("hidden");
    $deliveryLoadedEl.classList.add("flex");

    // Add fast delivery styling for today/tomorrow delivery
    if (isTimeRangeAvailable) {
      $deliveryLoadedEl.classList.add("border", "border-blue-700", "fast-delivery");
    } else {
      $deliveryLoadedEl.classList.remove("border", "border-blue-700", "fast-delivery");
    }
  });
};

const renderSameDayCompositions = rawCompositionIds => {
  const compositionIds = [...new Set(rawCompositionIds)];

  Array.from(compositionIds).forEach(compositionId => {
    const $sameDayContainer = document.querySelector(
      `.product-card__delivery-row[data-composition-id="${compositionId}"]`
    );
    const { sameDayCutoff: timeleft } = $sameDayContainer.dataset;
    renderEstimates({
      compositionId,
      date: SAME_DAY_DELIVERY_DATE,
      oneShipDay: true,
      orderInTheNext: timeleft,
      timeleft
    });
  });
};

export const loadCompositionEstimates = (rawCompositionIds, options) => {
  const compositionIds = [...new Set(rawCompositionIds)];
  const isMade = Boolean(options?.isMade);
  const isSendIndividually = Boolean(options?.isSendIndividually);
  const filteredCompositionIds = options?.isSameDayEstimates
    ? compositionIds
    : compositionIds.filter(compositionId => shouldProcessCompositionId(compositionId, options));

  if (filteredCompositionIds.length === 0) {
    return Promise.resolve([{}, 0]);
  }

  // Use individual requests only when isSendIndividually is true
  if (isSendIndividually) {
    // Send individual requests for each composition ID
    const promises = filteredCompositionIds.map(compositionId => {
      // Mark as processing
      markCompositionIdAsProcessing(compositionId, options);

      return fetcher
        .fetch([compositionId], options)
        .then(([data, ajaxTime]) => {
          if (data.success) {
            if (data.estimates[compositionId]) {
              const isFlash = Boolean(options?.isFlash);
              const isProductCardV3 = Boolean(options?.isProductCardV3);
              const {
                date,
                delivery_endtime: deliveryEndTime,
                inventory,
                one_ship_day: oneShipDay,
                shipping_rate: shippingRate,
                timeleft,
                delivery_day: deliveryDay,
                delivery_time: deliveryTime,
                delivery_time_end: deliveryTimeEnd,
                cutoff_label: cutoffLabel
              } = data.estimates[compositionId] || {};

              if (options?.isSameDayEstimates) {
                renderSameDayEstimates({
                  compositionId,
                  flashCutOffTime: timeleft,
                  deliveryDate: date,
                  deliveryTime,
                  deliveryTimeEnd,
                  cutoffLabel
                });
              } else if (isProductCardV3) {
                renderProductCardV3Estimates({
                  compositionId,
                  date,
                  deliveryTime,
                  deliveryTimeEnd,
                  cutoffLabel
                });
              } else {
                renderEstimates({
                  compositionId,
                  date,
                  inventory,
                  oneShipDay: isFlash || oneShipDay,
                  orderInTheNext: timeleft,
                  shippingRate,
                  timeleft: deliveryEndTime,
                  deliveryDay,
                  isMade,
                  deliveryTime,
                  deliveryTimeEnd,
                  cutoffLabel
                });
              }
            } else {
              // Handle empty estimates - still need to hide spinners
              const isProductCardV3 = Boolean(options?.isProductCardV3);

              if (options?.isSameDayEstimates) {
                renderSameDayEstimates({
                  compositionId,
                  flashCutOffTime: null,
                  deliveryDate: null,
                  deliveryTime: null,
                  deliveryTimeEnd: null
                });
              } else if (isProductCardV3) {
                renderProductCardV3Estimates({
                  compositionId,
                  date: null,
                  deliveryTime: null,
                  deliveryTimeEnd: null,
                  cutoffLabel: null
                });
              } else {
                renderEstimates({
                  compositionId,
                  date: null,
                  inventory: null,
                  oneShipDay: false,
                  orderInTheNext: null,
                  shippingRate: null,
                  timeleft: null,
                  deliveryDay: null,
                  isMade,
                  deliveryTime: null,
                  deliveryTimeEnd: null
                });
              }
            }
          }

          // Mark as completed
          markCompositionIdAsCompleted(compositionId, options);
          return [data, ajaxTime];
        })
        .catch(error => {
          // Mark as completed even on error
          markCompositionIdAsCompleted(compositionId, options);
          throw error;
        });
    });

    // Return a promise that resolves when all requests complete
    return Promise.all(promises).then(
      results =>
        // Return the first result for backward compatibility
        results[0] || [{}, 0]
    );
  }
  // Use traditional batch strategy for non-made compositions
  // Mark all as processing
  filteredCompositionIds.forEach(compositionId => {
    markCompositionIdAsProcessing(compositionId, options);
  });

  return fetcher
    .fetch(filteredCompositionIds, options)
    .then(([data, ajaxTime]) => {
      if (data.success) {
        filteredCompositionIds.forEach(compositionId => {
          if (data.estimates[compositionId]) {
            const isFlash = Boolean(options?.isFlash);
            const isProductCardV3 = Boolean(options?.isProductCardV3);
            const {
              date,
              delivery_endtime: deliveryEndTime,
              inventory,
              one_ship_day: oneShipDay,
              shipping_rate: shippingRate,
              timeleft,
              delivery_day: deliveryDay,
              delivery_time: deliveryTime,
              delivery_time_end: deliveryTimeEnd,
              cutoff_label: cutoffLabel
            } = data.estimates[compositionId] || {};

            if (options?.isSameDayEstimates) {
              renderSameDayEstimates({
                compositionId,
                flashCutOffTime: timeleft,
                deliveryDate: date,
                deliveryTime,
                deliveryTimeEnd,
                cutoffLabel
              });
            } else if (isProductCardV3) {
              renderProductCardV3Estimates({
                compositionId,
                date,
                deliveryTime,
                deliveryTimeEnd,
                cutoffLabel
              });
            } else {
              renderEstimates({
                compositionId,
                date,
                inventory,
                oneShipDay: isFlash || oneShipDay,
                orderInTheNext: timeleft,
                shippingRate,
                timeleft: deliveryEndTime,
                deliveryDay,
                isMade,
                deliveryTime,
                deliveryTimeEnd,
                cutoffLabel
              });
            }
          } else {
            // Handle empty estimates - still need to hide spinners
            const isProductCardV3 = Boolean(options?.isProductCardV3);

            if (options?.isSameDayEstimates) {
              renderSameDayEstimates({
                compositionId,
                flashCutOffTime: null,
                deliveryDate: null,
                deliveryTime: null,
                deliveryTimeEnd: null
              });
            } else if (isProductCardV3) {
              renderProductCardV3Estimates({
                compositionId,
                date: null,
                deliveryTime: null,
                deliveryTimeEnd: null,
                cutoffLabel: null
              });
            } else {
              renderEstimates({
                compositionId,
                date: null,
                inventory: null,
                oneShipDay: false,
                orderInTheNext: null,
                shippingRate: null,
                timeleft: null,
                deliveryDay: null,
                isMade,
                deliveryTime: null,
                deliveryTimeEnd: null
              });
            }
          }

          // Mark as completed
          markCompositionIdAsCompleted(compositionId, options);
        });
      }

      return [data, ajaxTime];
    })
    .catch(error => {
      // Mark all as completed even on error
      filteredCompositionIds.forEach(compositionId => {
        markCompositionIdAsCompleted(compositionId, options);
      });
      throw error;
    });
};

export const loadVisibleCompositionEstimates = () => {
  const estimatesRows = document.querySelectorAll(SELECTORS.PRODUCT_CARD_DELIVERY);

  const visibleRows = Array.from(estimatesRows).filter(
    element => isElementVisible(element) && !isSpinnerHidden(element)
  );

  const {
    regularCompositionIds,
    madeCompositionIds,
    sameDayCutoffIds,
    flashCompositionIds,
    businessLineMap
  } = getCompositionIds(visibleRows);

  let firstRequestCompleted = false;

  if (regularCompositionIds.length) {
    const groups = groupIdsByBusinessLine(regularCompositionIds, businessLineMap);
    Object.entries(groups).forEach(([bl, ids]) => {
      const props = {
        isMade: false,
        isFlash: false,
        isSendIndividually: !window.jiffy?.flip?.fastDeliveryEstimate,
        businessLine: bl
      };
      loadCompositionEstimates(ids, props).then(([_data]) => {
        if (!firstRequestCompleted) {
          firstRequestCompleted = true;
        }
      });
    });
  }

  if (madeCompositionIds.length) {
    madeCompositionIds.forEach(compositionId => {
      const businessLine = businessLineMap[compositionId];
      const props = {
        isMade: true,
        isFlash: false,
        isSendIndividually: !CompositionEstimatesClass.shouldUseFastEta(businessLine),
        businessLine
      };
      loadCompositionEstimates([compositionId], props).then(([_data]) => {
        if (!firstRequestCompleted) {
          firstRequestCompleted = true;
        }
      });
    });
  }

  if (flashCompositionIds.length) {
    flashCompositionIds.forEach(compositionId => {
      const businessLine = businessLineMap[compositionId];
      const props = {
        isMade: true,
        isFlash: true,
        isSendIndividually: !CompositionEstimatesClass.shouldUseFastEta(businessLine),
        businessLine
      };
      loadCompositionEstimates([compositionId], props).then(([_data]) => {
        if (!firstRequestCompleted) {
          firstRequestCompleted = true;
        }
      });
    });
  }

  if (sameDayCutoffIds.length) {
    renderSameDayCompositions(sameDayCutoffIds);
  }
};

const getVisibleItemsCount = $estimatesRows => {
  const visibleItemsCount = Array.from($estimatesRows).filter($el => {
    if ($el.dataset.flash === "true") {
      const $flash = $el.closest(".js-product-card").previousElementSibling;
      return isElementVisible($flash);
    }
    return isElementVisible($el) && !isSpinnerHidden($el);
  }).length;
  return visibleItemsCount === 0 ? 2 : visibleItemsCount;
};

export const loadFirstFewRowsEstimates = () => {
  const $estimatesRows = document.querySelectorAll(SELECTORS.PRODUCT_CARD_DELIVERY);

  const visibleItemsCount = getVisibleItemsCount($estimatesRows);

  // All with visible spinners but not necessarily visible in current viewport
  const $estimateableRows = Array.from($estimatesRows).filter($el => !isSpinnerHidden($el));

  // Get two packs of visibleItemsCount in a query that receives only 2 items at a time
  // For example, if we visibleItemsCount is 5 (desktop), we will get 5 queries with 2 items each
  // If visibleItemsCount is 2 (mobile), we will get 2 queries with 2 items each
  const $batches = chunkArray($estimateableRows, 2).slice(0, visibleItemsCount);

  let firstRequestCompleted = false;

  $batches.forEach($batch => {
    const {
      regularCompositionIds,
      madeCompositionIds,
      sameDayCutoffIds,
      flashCompositionIds,
      businessLineMap
    } = getCompositionIds($batch);

    if (regularCompositionIds.length) {
      const groups = groupIdsByBusinessLine(regularCompositionIds, businessLineMap);
      Object.entries(groups).forEach(([bl, ids]) => {
        const props = {
          isMade: false,
          isFlash: false,
          isSendIndividually: !window.jiffy?.flip?.fastDeliveryEstimate,
          businessLine: bl
        };
        loadCompositionEstimates(ids, props).then(([_data]) => {
          if (!firstRequestCompleted) {
            firstRequestCompleted = true;
          }
        });
      });
    }

    if (madeCompositionIds.length) {
      madeCompositionIds.forEach(compositionId => {
        const businessLine = businessLineMap[compositionId];
        const props = {
          isMade: true,
          isFlash: false,
          isSendIndividually: !CompositionEstimatesClass.shouldUseFastEta(businessLine),
          businessLine
        };
        loadCompositionEstimates([compositionId], props).then(([_data]) => {
          if (!firstRequestCompleted) {
            firstRequestCompleted = true;
          }
        });
      });
    }

    if (flashCompositionIds.length) {
      flashCompositionIds.forEach(compositionId => {
        const businessLine = businessLineMap[compositionId];
        const props = {
          isMade: true,
          isFlash: true,
          isSendIndividually: !CompositionEstimatesClass.shouldUseFastEta(businessLine),
          businessLine
        };
        loadCompositionEstimates([compositionId], props).then(([_data]) => {
          if (!firstRequestCompleted) {
            firstRequestCompleted = true;
          }
        });
      });
    }

    if (sameDayCutoffIds.length) {
      renderSameDayCompositions(sameDayCutoffIds);
    }
  });
};

export const initSameDayBadges = () => {
  const $elements = document.querySelectorAll(SELECTORS.SAME_DAY_BADGES);

  if ($elements.length === 0) {
    return;
  }

  const elementsArray = Array.from($elements);
  const blMap = {};
  elementsArray.forEach(el => {
    const { compositionId, businessLine } = el.dataset;
    if (compositionId && businessLine) blMap[compositionId] = businessLine;
  });
  const compositionIds = elementsArray.map(item => item.dataset.compositionId);
  const groups = groupIdsByBusinessLine(compositionIds, blMap);
  Object.entries(groups).forEach(([bl, ids]) => {
    loadCompositionEstimates(ids, {
      isSameDayEstimates: true,
      isSendIndividually: !window.jiffy?.flip?.fastDeliveryEstimate,
      businessLine: bl
    });
  });
};
